import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useGroupGoalsOverview } from '../hooks/useGroupGoals';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HeaderSubtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: var(--onSurfaceVariant);
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
`;

const Metric = styled.div`
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.75rem;
`;

const MetricLabel = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetricValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const BarTrack = styled.div`
  margin-top: 0.8rem;
  width: 100%;
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--outline);
  display: flex;
`;

const BarSegment = styled.div<{ $width: number; $color: string }>`
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $color }) => $color};
  transition: width 0.25s ease;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.6rem;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
`;

const Dot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $color }) => $color};
  margin-right: 0.35rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid var(--outline);
    font-size: 0.9rem;
    color: var(--onSurface);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const TableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MiniBarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MiniBarTrack = styled.div`
  width: 120px;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--outline);
  display: flex;
`;

const MiniBarText = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const ViewGoalsHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.65rem;
  color: var(--primary);
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
`;

const ArrowIcon = styled.span`
  font-size: 0.9rem;
  line-height: 1;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 200;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(760px, 96vw);
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.1rem;
  cursor: pointer;
  color: var(--onSurfaceVariant);
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  overflow: auto;
`;

const GoalsList = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const GoalItem = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.75rem;
  background: var(--surfaceVariant);
`;

const GoalTitle = styled.p`
  margin: 0 0 0.35rem;
  color: var(--onSurface);
  font-weight: 600;
`;

const GoalMeta = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const GoalDescription = styled.p`
  margin: 0 0 0.45rem;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  white-space: pre-wrap;
`;

const EmptyText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
`;

const colors = {
  achieved: 'var(--success)',
  notAchieved: 'var(--error)',
  pending: 'var(--onSurfaceVariant)',
};

export default function GroupGoalsPage() {
  const { data, isLoading, error } = useGroupGoalsOverview();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const overall = data?.overall;
  const byGroup = data?.byGroup ?? [];
  const selectedGroup = byGroup.find((group) => group.groupId === selectedGroupId) ?? null;

  const selectedGroupGoals = useMemo(() => {
    if (!selectedGroupId) return [];
    return (data?.goals ?? [])
      .filter((goal) => goal.group_id === selectedGroupId)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [data?.goals, selectedGroupId]);

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getGoalStatusLabel = (status: string) => {
    if (status === 'achieved') return 'Achieved';
    if (status === 'not_achieved') return 'Not achieved';
    return 'Pending';
  };

  if (isLoading) return <Page><HeaderTitle>Group Goals</HeaderTitle><p>Loading goals…</p></Page>;
  if (error) return <Page><HeaderTitle>Group Goals</HeaderTitle><p>Error loading group goals.</p></Page>;

  return (
    <Page>
      <HeaderTitle>Group Goals</HeaderTitle>
      <HeaderSubtitle>
        Track goal completion by group, including overdue goals counted as not achieved.
      </HeaderSubtitle>

      <Card>
        <SummaryGrid>
          <Metric>
            <MetricLabel>Total Goals</MetricLabel>
            <MetricValue>{overall?.total ?? 0}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Achieved</MetricLabel>
            <MetricValue>{overall?.achievedPct ?? 0}%</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Not Achieved</MetricLabel>
            <MetricValue>{overall?.notAchievedPct ?? 0}%</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Pending</MetricLabel>
            <MetricValue>{overall?.pending ?? 0}</MetricValue>
          </Metric>
        </SummaryGrid>

        <BarTrack>
          <BarSegment $width={overall?.achievedPct ?? 0} $color={colors.achieved} />
          <BarSegment $width={overall?.notAchievedPct ?? 0} $color={colors.notAchieved} />
          <BarSegment
            $width={Math.max(0, 100 - ((overall?.achievedPct ?? 0) + (overall?.notAchievedPct ?? 0)))}
            $color={colors.pending}
          />
        </BarTrack>

        <LegendRow>
          <span><Dot $color={colors.achieved} />Achieved</span>
          <span><Dot $color={colors.notAchieved} />Not achieved</span>
          <span><Dot $color={colors.pending} />Pending</span>
        </LegendRow>
      </Card>

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Group</th>
              <th>Total</th>
              <th>Achieved</th>
              <th>Not Achieved</th>
              <th>Pending</th>
              <th>Chart</th>
            </tr>
          </thead>
          <tbody>
            {byGroup.map((group) => (
              <TableRow
                key={group.groupId}
                onClick={() => setSelectedGroupId(group.groupId)}
                title="View goals"
              >
                <td>{group.groupName}</td>
                <td>{group.total}</td>
                <td>{group.achieved} ({group.achievedPct}%)</td>
                <td>{group.notAchieved} ({group.notAchievedPct}%)</td>
                <td>{group.pending}</td>
                <td>
                  <MiniBarWrap>
                    <MiniBarTrack>
                      <BarSegment $width={group.achievedPct} $color={colors.achieved} />
                      <BarSegment $width={group.notAchievedPct} $color={colors.notAchieved} />
                      <BarSegment
                        $width={Math.max(0, 100 - (group.achievedPct + group.notAchievedPct))}
                        $color={colors.pending}
                      />
                    </MiniBarTrack>
                    <MiniBarText>{group.achievedPct}% / {group.notAchievedPct}%</MiniBarText>
                    <ViewGoalsHint>
                      View goals <ArrowIcon>›</ArrowIcon>
                    </ViewGoalsHint>
                  </MiniBarWrap>
                </td>
              </TableRow>
            ))}
          </tbody>
          </Table>
        </TableWrap>
      </Card>

      {selectedGroup && (
        <ModalOverlay onClick={() => setSelectedGroupId(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedGroup.groupName} Goals</ModalTitle>
              <ModalCloseButton type="button" onClick={() => setSelectedGroupId(null)}>
                ✕
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {selectedGroupGoals.length === 0 ? (
                <EmptyText>No goals found for this group.</EmptyText>
              ) : (
                <GoalsList>
                  {selectedGroupGoals.map((goal) => (
                    <GoalItem key={goal.id}>
                      <GoalTitle>{goal.title}</GoalTitle>
                      {goal.description ? <GoalDescription>{goal.description}</GoalDescription> : null}
                      <GoalMeta>
                        Due: {formatDate(goal.due_date)} · Status: {getGoalStatusLabel(goal.status)}
                      </GoalMeta>
                    </GoalItem>
                  ))}
                </GoalsList>
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
}
